// src/components/AboutUs.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";

const InfoCard = ({ eyebrow, title, description, className = "" }) => (
  <div
    className={`rounded-2xl border border-slate-100 bg-white p-5 sm:p-6 ${className}`}
  >
    {eyebrow && (
      <p className="text-xs font-semibold tracking-[0.2em] text-sky-600 uppercase">
        {eyebrow}
      </p>
    )}
    <h3 className="mt-3 text-base sm:text-lg font-semibold text-slate-900">
      {title}
    </h3>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{description}</p>
  </div>
);
const AboutUs = () => {
  const { t } = useTranslation();

  const howWeHelpItems = [
    {
      eyebrow: t("about.howWeHelp.items.1.eyebrow"),
      title: t("about.howWeHelp.items.1.title"),
      description: t("about.howWeHelp.items.1.description"),
    },
    {
      eyebrow: t("about.howWeHelp.items.2.eyebrow"),
      title: t("about.howWeHelp.items.2.title"),
      description: t("about.howWeHelp.items.2.description"),
    },
    {
      eyebrow: t("about.howWeHelp.items.3.eyebrow"),
      title: t("about.howWeHelp.items.3.title"),
      description: t("about.howWeHelp.items.3.description"),
    },
  ];

  const valuesItems = [
    {
      title: t("about.values.transparency.title"),
      description: t("about.values.transparency.description"),
    },
    {
      title: t("about.values.accountability.title"),
      description: t("about.values.accountability.description"),
    },
    {
      title: t("about.values.anticorruption.title"),
      description: t("about.values.anticorruption.description"),
    },
    {
      title: t("about.values.quality.title"),
      description: t("about.values.quality.description"),
    },
  ];

  // FAQ items from i18n
  const faqs = [
    {
      question: t("about.faq.q1.question"),
      answer: t("about.faq.q1.answer"),
    },
    {
      question: t("about.faq.q2.question"),
      answer: t("about.faq.q2.answer"),
    },
    {
      question: t("about.faq.q3.question"),
      answer: t("about.faq.q3.answer"),
    },
    {
      question: t("about.faq.q4.question"),
      answer: t("about.faq.q4.answer"),
    },
    {
      question: t("about.faq.q5.question"),
      answer: t("about.faq.q5.answer"),
    },
    {
      question: t("about.faq.q6.question"),
      answer: t("about.faq.q6.answer"),
    },
    {
      question: t("about.faq.q7.question"),
      answer: t("about.faq.q7.answer"),
    },
    {
      question: t("about.faq.q8.question"),
      answer: t("about.faq.q8.answer"),
    },
    {
      question: t("about.faq.q9.question"),
      answer: t("about.faq.q9.answer"),
    },
  ];

  // FAQ search state
  const [faqQuery, setFaqQuery] = React.useState("");

  const filteredFaqs = faqs.filter((faq) => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      faq.question.toLowerCase().includes(q) ||
      faq.answer.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 via-slate-50 to-sky-100 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] text-sky-600 uppercase">
            {t("about.hero.eyebrow")}
          </p>

          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            {t("about.hero.title.part1")}
            <span className="text-sky-600">{t("about.hero.title.part2")}</span>
          </h1>

          <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-3xl leading-relaxed">
            {t("about.hero.description")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/report">{t("nav.reportIssue")}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/reports">{t("home.browseIssues")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mission + Story */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">{t("about.mission.title")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t("about.mission.p1")}</p>
            <p className="mt-3 text-slate-600 leading-relaxed">{t("about.mission.p2")}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 lg:p-8">
            <h3 className="text-sm font-semibold text-sky-600 uppercase tracking-[0.18em]">
              {t("about.story.title")}
            </h3>

            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
              {t("about.story.p1")}
            </p>

            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
              {t("about.story.p2")}
            </p>

            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
              {t("about.story.p3")}
            </p>

            <dl className="mt-6 grid grid-cols-2 gap-4 text-xs sm:text-sm">
              <div>
                <dt className="text-slate-500">{t("about.story.builtBy")}</dt>
                <dd className="font-semibold text-slate-900">{t("about.story.builtBy.value")}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{t("about.story.focus")}</dt>
                <dd className="font-semibold text-slate-900"> {t("about.story.focus.value")}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{t("about.story.approach")}</dt>
                <dd className="font-semibold text-slate-900">
                  {t("about.story.approach.value")}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">{t("about.story.vision")}</dt>
                <dd className="font-semibold text-slate-900">
                  {t("about.story.vision.value")}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
            {t("about.howWeHelp.title")}
          </h2>
          <p className="mt-2 text-slate-600 max-w-2xl">
            {t("about.howWeHelp.subtitle")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {howWeHelpItems.map((item) => (
              <InfoCard key={item.eyebrow} eyebrow={item.eyebrow} title={item.title} description={item.description}/>
            ))}
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-12 lg:py-16 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
            {t("about.values.title")}
          </h2>
          <p className="mt-2 text-slate-600 max-w-2xl">
            {t("about.values.subtitle")}
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {valuesItems.map((item) => (
              <div key={item.title} className="bg-slate-50 rounded-2xl border border-slate-100 p-5">
                <h3 className="font-semibold text-slate-900">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 lg:py-16 border-t border-slate-100 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
            {t("about.cta.title")}
          </h2>
          <p className="mt-3 text-slate-600">
            {t("about.cta.description")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link to="/report">{t("nav.reportIssue")}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/reports">{t("home.browseIssues")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 lg:py-14 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">
            {t("about.faq.title")}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {t("about.faq.subtitle")}
          </p>

          {/* FAQ search */}
          <div className="mt-4">
            <input
              type="text"
              value={faqQuery}
              onChange={(e) => setFaqQuery(e.target.value)}
              placeholder={t("about.faq.searchPlaceholder")}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
            />
          </div>

          <div className="mt-6 space-y-2.5">
            {filteredFaqs.length === 0 && (
              <p className="text-xs sm:text-sm text-slate-500">
                {t("about.faq.noResults")}
              </p>
            )}

            {filteredFaqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 sm:px-4 sm:py-3"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-slate-900">
                    {faq.question}
                  </span>
                  <span className="text-slate-400 text-lg transition-transform duration-200 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
